import React from "react";
import { Link } from "react-router-dom";
import { Edit, Trash2, Eye } from "lucide-react";
import { ENTITY_NAMES } from "../../constants/entities";
import { isRunCompleted, formatDaysOfWeek } from "../../utils/runs";

interface Student {
  id: string;
  codername: string;
  age: number;
  parentname: string;
  email: string;
  phone: string;
  run?: {
    id: string;
    name: string;
    day_of_week: string[];
    start_time: string;
    lab_dates?: Array<{
      id: string;
      run_id: string;
      lab_number: number;
      date: string;
    }>;
    series: {
      id: string;
      title: string;
      code: string;
    };
  };
}

interface Props {
  students: Student[];
  isAdmin: boolean;
  onDeleteStudent?: (id: string) => void;
  handleSort?: (field: string) => void;
  sortField?: string;
  sortDirection?: "asc" | "desc";
}

const StudentsList: React.FC<Props> = ({
  students,
  isAdmin,
  onDeleteStudent,
  handleSort,
}) => {
  const formatTime = (timeString: string) => {
    if (!timeString) return "";
    const [hours, minutes] = timeString.split(":");
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-neutral-200">
        <thead className="bg-neutral-50">
          <tr>
            <th
              onClick={() => handleSort?.("studentName")}
              className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700"
            >
              Student Info
            </th>
            <th
              onClick={() => handleSort?.("parentName")}
              className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700"
            >
              Parent Info
            </th>
            <th
              onClick={() => handleSort?.("series")}
              className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider cursor-pointer hover:text-neutral-700"
            >
              Current {ENTITY_NAMES.RUN}
            </th>
            {isAdmin && (
              <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-neutral-200">
          {students.map((student) => (
            <tr key={student.id} className="hover:bg-neutral-50">
              <td className="px-6 py-4">
                <Link
                  to={`/students/${student.id}`}
                  className="font-medium hover:text-primary-600"
                >
                  {student.codername}
                </Link>
                <p className="text-sm text-neutral-500">Age {student.age}</p>
              </td>
              <td className="px-6 py-4">
                <p className="text-sm font-medium">{student.parentname}</p>
                <p className="text-sm text-neutral-500">
                  <a
                    href={`mailto:${student.email}`}
                    className="hover:text-primary-600"
                  >
                    {student.email}
                  </a>
                </p>
                <p className="text-sm text-neutral-500">
                  <a
                    href={`tel:${student.phone}`}
                    className="hover:text-primary-600"
                  >
                    {student.phone}
                  </a>
                </p>
              </td>
              <td className="px-6 py-4">
                {student.run ? (
                  <div>
                    {/* Check if the run is completed */}
                    {isRunCompleted({
                      active: true, // Assume active since student is enrolled
                      lab_dates: student.run.lab_dates || [],
                    }) ? (
                      <span className="text-sm text-neutral-500">
                        Not in an active group
                      </span>
                    ) : (
                      <div>
                        <Link
                          to={`/runs/${student.run.id}`}
                          className="text-sm hover:text-primary-600"
                        >
                          {ENTITY_NAMES.RUN} {student.run.name}
                        </Link>
                        <span className="text-sm"> of </span>
                        <Link
                          to={`/series/${student.run.series.id}`}
                          className="text-sm hover:text-primary-600"
                        >
                          {student.run.series.code}
                        </Link>
                        <p className="text-sm text-neutral-500">
                          {formatDaysOfWeek(student.run.day_of_week)} at{" "}
                          {formatTime(student.run.start_time)}
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <span className="text-sm text-neutral-500">Not enrolled</span>
                )}
              </td>
              {isAdmin && (
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end space-x-2">
                    <Link
                      to={`/students/${student.id}`}
                      className="icon-view"
                      title="View details"
                    >
                      <Eye className="h-5 w-5" />
                    </Link>
                    <Link
                      to={`/students/${student.id}/edit`}
                      className="icon-edit"
                      title="Edit student"
                    >
                      <Edit className="h-5 w-5" />
                    </Link>
                    {onDeleteStudent && (
                      <button
                        onClick={() => onDeleteStudent(student.id)}
                        className="icon-delete"
                        title="Delete student"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default StudentsList;
